import {
  Add,
  ChevronRight,
  Delete,
  Edit,
  ExpandMore,
  MenuBook,
  PlayCircleOutline,
  Search,
  YouTube,
} from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { hostname } from "../../utils";
import DocumentationPageDialog from "./dialogs/DocumentationPageDialog";

interface DocumentationPage {
  id: string;
  title: string;
  slug: string;
  content: string;
  parentId: string | null;
  sortOrder: number;
  icon: string | null;
  children?: DocumentationPage[];
}

interface DocumentationProps {
  getToken: () => Promise<string>;
  isSuperUser?: boolean;
  eventListener?: (event: { type: string; error?: string; message?: string }) => void;
}

const Documentation: React.FC<DocumentationProps> = ({
  getToken,
  isSuperUser = false,
  eventListener,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { whitelabelSettings } = useWhitelabel();
  const isMainPlatform =
    !whitelabelSettings || whitelabelSettings.isMainSite === true;

  const [pages, setPages] = useState<DocumentationPage[]>([]);
  const [selectedPage, setSelectedPage] = useState<DocumentationPage | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Super user editing state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<DocumentationPage | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [deleteConfirmPage, setDeleteConfirmPage] =
    useState<DocumentationPage | null>(null);


  useEffect(() => {
    loadDocumentation();
  }, [whitelabelSettings]);

  // Debounced search handler
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 300);
  }, []);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const loadDocumentation = async () => {
    setLoading(true);
    try {
      const whitelabelId = whitelabelSettings?.id;
      const url = whitelabelId
        ? `${hostname}/documentation?whitelabelId=${whitelabelId}`
        : `${hostname}/documentation`;

      const response = await axios.get(url);
      setPages(response.data);

      // Select the first page by default
      if (response.data.length > 0 && !selectedPage) {
        selectFirstPage(response.data);
      }
    } catch (err) {
      console.error("Error loading documentation:", err);
    }
    setLoading(false);
  };

  const selectFirstPage = (pageTree: DocumentationPage[]) => {
    // Find the first page (could be a top-level page or first child)
    if (pageTree.length > 0) {
      const firstPage = pageTree[0];
      if (firstPage.content) {
        setSelectedPage(firstPage);
      } else if (firstPage.children && firstPage.children.length > 0) {
        setSelectedPage(firstPage.children[0]);
        setExpandedItems(new Set([firstPage.id]));
      }
    }
  };

  // Super user CRUD operations
  const handleCreatePage = () => {
    setEditingPage(null);
    setDialogOpen(true);
  };

  const handleEditPage = (page: DocumentationPage, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setEditingPage(page);
    setDialogOpen(true);
  };

  const handleDeletePage = async (page: DocumentationPage) => {
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/documentation/admin/${page.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteConfirmPage(null);
      // If the deleted page was selected, clear selection
      if (selectedPage?.id === page.id) {
        setSelectedPage(null);
      }
      await loadDocumentation();
    } catch (err) {
      console.error("Error deleting documentation page:", err);
    }
  };

  const handleSavePage = async (pageData: Partial<DocumentationPage>) => {
    setSaving(true);
    try {
      const token = await getToken();
      const whitelabelId = whitelabelSettings?.id;

      if (editingPage) {
        // Update existing page
        await axios.put(
          `${hostname}/documentation/admin/${editingPage.id}`,
          {
            ...pageData,
            whitelabelId,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        // Create new page
        await axios.post(
          `${hostname}/documentation/admin`,
          {
            ...pageData,
            whitelabelId,
            isPublished: true,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      setDialogOpen(false);
      setEditingPage(null);
      await loadDocumentation();

      // If we edited the currently selected page, update it
      if (editingPage && selectedPage?.id === editingPage.id) {
        // Reload to get the updated page
        const response = await axios.get(
          `${hostname}/documentation/${editingPage.id}`
        );
        setSelectedPage(response.data);
      }
    } catch (err: any) {
      console.error("Error saving documentation page:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to save page";
      eventListener?.({
        type: "SET_ERROR",
        error: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePageSelect = (page: DocumentationPage) => {
    setSelectedPage(page);
    if (isMobile) {
      setMobileNavOpen(false);
    }
  };

  const handleToggleExpand = (pageId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pageId)) {
        newSet.delete(pageId);
      } else {
        newSet.add(pageId);
      }
      return newSet;
    });
  };

  // Filter pages based on search query
  const filterPages = (
    pageList: DocumentationPage[],
    query: string
  ): DocumentationPage[] => {
    if (!query) return pageList;

    const lowerQuery = query.toLowerCase();

    return pageList.reduce<DocumentationPage[]>((acc, page) => {
      const titleMatch = page.title.toLowerCase().includes(lowerQuery);
      const contentMatch = page.content?.toLowerCase().includes(lowerQuery);
      const filteredChildren = page.children
        ? filterPages(page.children, query)
        : [];

      if (titleMatch || contentMatch || filteredChildren.length > 0) {
        acc.push({
          ...page,
          children: filteredChildren,
        });
      }

      return acc;
    }, []);
  };

  const filteredPages = useMemo(
    () => filterPages(pages, searchQuery),
    [pages, searchQuery]
  );

  // Helper to get the first page from a tree (including children)
  const getFirstPage = useCallback(
    (pageList: DocumentationPage[]): DocumentationPage | null => {
      if (pageList.length === 0) return null;
      const firstPage = pageList[0];
      // If first page has content, return it; otherwise check children
      if (firstPage.content) {
        return firstPage;
      } else if (firstPage.children && firstPage.children.length > 0) {
        return firstPage.children[0];
      }
      return firstPage;
    },
    []
  );

  // Auto-select first matching page and expand all matching parents when search results change
  useEffect(() => {
    if (searchQuery && filteredPages.length > 0) {
      const firstMatch = getFirstPage(filteredPages);
      if (firstMatch) {
        setSelectedPage(firstMatch);
      }
      // Expand all pages that have children in the filtered results
      const parentIds = filteredPages
        .filter((page) => page.children && page.children.length > 0)
        .map((page) => page.id);
      if (parentIds.length > 0) {
        setExpandedItems((prev) => new Set([...prev, ...parentIds]));
      }
    }
  }, [searchQuery, filteredPages, getFirstPage]);

  // Build breadcrumbs for the selected page
  const getBreadcrumbs = (
    pageTree: DocumentationPage[],
    targetId: string,
    path: DocumentationPage[] = []
  ): DocumentationPage[] | null => {
    for (const page of pageTree) {
      if (page.id === targetId) {
        return [...path, page];
      }
      if (page.children) {
        const result = getBreadcrumbs(page.children, targetId, [...path, page]);
        if (result) return result;
      }
    }
    return null;
  };

  const breadcrumbs = selectedPage
    ? getBreadcrumbs(pages, selectedPage.id) || []
    : [];

  // Find a page by slug in the tree
  const findPageBySlug = useCallback(
    (
      slug: string,
      pageList: DocumentationPage[] = pages
    ): DocumentationPage | null => {
      for (const page of pageList) {
        if (page.slug === slug) {
          return page;
        }
        if (page.children) {
          const found = findPageBySlug(slug, page.children);
          if (found) return found;
        }
      }
      return null;
    },
    [pages]
  );

  // Handle internal documentation links
  const handleLinkClick = useCallback(
    (href: string, event: React.MouseEvent) => {
      // Check if it's an internal documentation link (ends with .md or is a relative path)
      if (
        href.endsWith(".md") ||
        (!href.startsWith("http") && !href.startsWith("mailto:"))
      ) {
        event.preventDefault();

        // Extract slug from href (remove .md extension and path)
        let slug =
          href
            .replace(/\.md$/, "")
            .replace(/^\.\//, "")
            .replace(/^\//, "")
            .split("/")
            .pop() || "";

        // Remove any anchor links
        slug = slug.split("#")[0];

        const targetPage = findPageBySlug(slug);
        if (targetPage) {
          handlePageSelect(targetPage);
          // Expand parent if needed
          if (targetPage.parentId) {
            setExpandedItems(
              (prev) => new Set([...prev, targetPage.parentId!])
            );
          }
        }
      }
      // External links will open normally
    },
    [findPageBySlug, handlePageSelect]
  );

  // Custom components for ReactMarkdown to handle links
  const markdownComponents: Components = useMemo(
    () => ({
      a: ({ href, children, ...props }) => {
        const isExternal =
          href?.startsWith("http") || href?.startsWith("mailto:");
        return (
          <a
            href={href}
            onClick={(e) => href && handleLinkClick(href, e)}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            style={{ cursor: "pointer" }}
            {...props}
          >
            {children}
          </a>
        );
      },
    }),
    [handleLinkClick]
  );

  const renderNavItem = (page: DocumentationPage, depth: number = 0) => {
    const hasChildren = page.children && page.children.length > 0;
    const isExpanded = expandedItems.has(page.id);
    const isSelected = selectedPage?.id === page.id;

    return (
      <React.Fragment key={page.id}>
        <ListItemButton
          onClick={() => handlePageSelect(page)}
          selected={isSelected}
          sx={{
            pl: 2 + depth * 2,
            borderRadius: 1,
            mb: 0.5,
            "&.Mui-selected": {
              backgroundColor: theme.palette.primary.main + "20",
            },
            "&:hover .edit-actions": {
              opacity: 1,
            },
          }}
        >
          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => handleToggleExpand(page.id, e)}
              sx={{ mr: 1, p: 0.5 }}
            >
              {isExpanded ? (
                <ExpandMore fontSize="small" />
              ) : (
                <ChevronRight fontSize="small" />
              )}
            </IconButton>
          )}
          {!hasChildren && <Box sx={{ width: 28 }} />}
          <ListItemText
            primary={page.title}
            primaryTypographyProps={{
              variant: depth === 0 ? "subtitle2" : "body2",
              fontWeight: depth === 0 ? 600 : 400,
            }}
          />
          {isSuperUser && (
            <Box
              className="edit-actions"
              sx={{
                display: "flex",
                gap: 0.5,
                opacity: 0,
                transition: "opacity 0.2s",
              }}
            >
              <Tooltip title="Edit page">
                <IconButton
                  size="small"
                  onClick={(e) => handleEditPage(page, e)}
                  sx={{ p: 0.5 }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete page">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmPage(page);
                  }}
                  sx={{ p: 0.5, color: "error.main" }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </ListItemButton>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List disablePadding>
              {page.children!.map((child) => renderNavItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const renderNavigationContent = () => (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Documentation
        </Typography>
        {isSuperUser && (
          <Tooltip title="Add new page">
            <IconButton size="small" onClick={handleCreatePage} color="primary">
              <Add />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Search documentation..."
        value={searchInput}
        onChange={(e) => handleSearchChange(e.target.value)}
        autoComplete="off"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />
      <List disablePadding>
        {filteredPages.map((page) => renderNavItem(page))}
      </List>
      {isMainPlatform && (
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <ListItemButton
            component="a"
            href="/training"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <PlayCircleOutline fontSize="small" sx={{ mr: 1.5 }} />
            <ListItemText
              primary="Video Training"
              primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
            />
          </ListItemButton>
          <ListItemButton
            component="a"
            href="https://www.youtube.com/channel/UCQOZPsN_LNaRy_tfp7YaiKA"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ borderRadius: 1 }}
          >
            <YouTube fontSize="small" sx={{ mr: 1.5 }} />
            <ListItemText
              primary="YouTube Channel"
              primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
            />
          </ListItemButton>
        </Box>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (pages.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <MenuBook sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Documentation Available
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Documentation pages have not been set up yet.
          </Typography>
          {isSuperUser && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreatePage}
            >
              Create First Page
            </Button>
          )}
        </Paper>

        {/* Dialog for creating pages */}
        <DocumentationPageDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditingPage(null);
          }}
          onSave={handleSavePage}
          page={editingPage}
          pages={pages}
          loading={saving}
        />
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "calc(100vh - 64px)",
      }}
    >
      {/* Desktop Navigation Sidebar */}
      {!isMobile && (
        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            overflowY: "auto",
            maxHeight: "calc(100vh - 64px)",
            position: "sticky",
            top: 64,
          }}
        >
          {renderNavigationContent()}
        </Box>
      )}

      {/* Mobile Navigation Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          PaperProps={{
            sx: { width: 280 },
          }}
        >
          {renderNavigationContent()}
        </Drawer>
      )}

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4, lg: 6 } }}>
          {/* Mobile menu button */}
          {isMobile && (
            <Box sx={{ mb: 2 }}>
              <IconButton onClick={() => setMobileNavOpen(true)} sx={{ mr: 1 }}>
                <MenuBook />
              </IconButton>
              <Typography variant="caption" color="text.secondary">
                Browse Documentation
              </Typography>
            </Box>
          )}

          {/* Breadcrumbs */}
          {breadcrumbs.length > 1 && (
            <Breadcrumbs sx={{ mb: 2 }}>
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return isLast ? (
                  <Typography key={crumb.id} color="text.primary">
                    {crumb.title}
                  </Typography>
                ) : (
                  <Link
                    key={crumb.id}
                    component="button"
                    variant="body2"
                    onClick={() => handlePageSelect(crumb)}
                    sx={{ cursor: "pointer" }}
                    underline="hover"
                  >
                    {crumb.title}
                  </Link>
                );
              })}
            </Breadcrumbs>
          )}

          {/* Page Content */}
          {selectedPage ? (
            <Paper sx={{ p: { xs: 2, md: 4 } }}>
              {isSuperUser && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: 2,
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEditPage(selectedPage)}
                  >
                    Edit Page
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => setDeleteConfirmPage(selectedPage)}
                  >
                    Delete
                  </Button>
                </Box>
              )}
              <Box
                className="markdown-content"
                sx={{
                  "& h1": {
                    fontSize: "2rem",
                    fontWeight: 700,
                    mb: 2,
                    mt: 0,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    pb: 1,
                  },
                  "& h2": {
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    mt: 4,
                    mb: 2,
                  },
                  "& h3": {
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    mt: 3,
                    mb: 1.5,
                  },
                  "& h4": {
                    fontSize: "1rem",
                    fontWeight: 600,
                    mt: 2,
                    mb: 1,
                  },
                  "& p": {
                    mb: 2,
                    lineHeight: 1.7,
                  },
                  "& ul, & ol": {
                    mb: 2,
                    pl: 3,
                  },
                  "& li": {
                    mb: 0.5,
                    lineHeight: 1.7,
                  },
                  "& code": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    padding: "2px 6px",
                    borderRadius: 1,
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  },
                  "& pre": {
                    backgroundColor: theme.palette.grey[900],
                    color: theme.palette.grey[100],
                    p: 2,
                    borderRadius: 1,
                    overflow: "auto",
                    mb: 2,
                    "& code": {
                      backgroundColor: "transparent",
                      padding: 0,
                      color: "inherit",
                    },
                  },
                  "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                    mb: 2,
                  },
                  "& th, & td": {
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    textAlign: "left",
                    color: theme.palette.text.primary,
                  },
                  "& th": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    fontWeight: 600,
                  },
                  "& tr:nth-of-type(even)": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[900]
                        : theme.palette.grey[50],
                  },
                  "& blockquote": {
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    pl: 2,
                    ml: 0,
                    color: theme.palette.text.secondary,
                    fontStyle: "italic",
                  },
                  "& hr": {
                    border: "none",
                    borderTop: `1px solid ${theme.palette.divider}`,
                    my: 3,
                  },
                  "& a": {
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  },
                  "& img": {
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: 1,
                  },
                }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {selectedPage.content}
                </ReactMarkdown>
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                Select a page from the navigation to view its content.
              </Typography>
            </Paper>
          )}
        </Container>
      </Box>

      {/* Documentation Page Dialog */}
      <DocumentationPageDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingPage(null);
        }}
        onSave={handleSavePage}
        page={editingPage}
        pages={pages}
        loading={saving}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmPage}
        onClose={() => setDeleteConfirmPage(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Documentation Page</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteConfirmPage?.title}"?
          </Typography>
          {deleteConfirmPage?.children && deleteConfirmPage.children.length > 0 && (
            <Typography color="error" sx={{ mt: 2 }}>
              Warning: This page has {deleteConfirmPage.children.length} child
              page(s) that will also be affected.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmPage(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteConfirmPage && handleDeletePage(deleteConfirmPage)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Documentation;
